import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import {
  CheckIcon,
  ChatBubbleLeftIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/solid';
import PageContainer from '../Container';
import Heading from '../../components/Heading';
import TextField from '../../components/TextField';
import { PrimaryButton } from '../../components/Button';
import MedicineRowCell from '../../components/MedicineRowCell';
import ListWrapper from '../../components/ListWrapper';
import Link from '../../components/Link';
import { Bag, Capsule } from '../../components/Icons/Medicines';
import { fetchAccess, submitConsent, initialState, normaliseUKPhoneNumber } from '../../redux/modules/nms';
import { getNMSState } from '../../redux/modules/root-reducer';
import {
  Content,
  Illustration,
  Introduction,
  Section,
  SectionTitle,
  Medicines,
  Medicine,
  Copy,
  Hint,
  SupportForm,
  Actions,
  ErrorMessage,
  Confirmation,
  ConfirmationIcon,
  ConfirmationTitle,
  Callout,
  NextSteps,
  Footer,
} from './styles';

const LinkExpiredIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M12 5v9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="12" cy="18" r="1.5" fill="currentColor" />
  </svg>
);

const previewMedicines = [1, 2, 3].map(number => ({
  name: `New medication ${number}`,
  vmpId: `preview-${number}`,
  form: 'capsule',
  prodDesc: '',
}));

const linkStates = {
  unavailable: {
    title: 'This Link is No Longer Available',
    message: 'We can’t use this link. It may be invalid, have expired or already have been used.',
    nextStep: 'If you’d still like support with your medication, please contact our pharmacy team.',
  },
  expired: {
    title: 'This Link has Expired',
    message: 'For security reasons, this link is no longer valid.',
    nextStep: 'If you’d still like support with your new medication, please contact our pharmacy team.',
  },
  'already-responded': {
    title: 'You’ve Already Responded',
    message: "We’ve already received a response using this link, you don't need to complete this form again.",
    nextStep: 'If you need help with your medication, please contact our pharmacy team.',
  },
};

const getToken = props => props.match.params.token || '';

export class NMSPage extends Component {
  constructor(props) {
    super(props);
    this.state = { contactNumber: '', error: '', selection: props.initialSelection, token: getToken(props) };
    this.pendingTokens = new Set();
  }

  componentDidMount() {
    this.loadAccess();
  }

  static getDerivedStateFromProps(props, state) {
    const token = getToken(props);
    return token === state.token ? null : { token, contactNumber: '', error: '', selection: '' };
  }

  componentDidUpdate(prevProps) {
    const { nms } = this.props;
    if (getToken(prevProps) !== getToken(this.props)) {
      this.loadAccess();
    } else if (prevProps.nms.status !== nms.status) {
      this.focusTitle();
    }
  }

  focusTitle = () => {
    const title = document.getElementById('nms-confirmation-title');
    if (title) title.focus();
  };

  loadAccess = () => {
    const { preview, fetchAccess: load } = this.props;
    const token = getToken(this.props);
    if (!preview && token) load(token);
  };

  canSubmit = () => {
    const { preview, nms } = this.props;
    return !this.pendingTokens.has(getToken(this.props)) && (preview
      || (nms.token === getToken(this.props) && nms.status === 'active' && !nms.submitting));
  };

  showConfirmation = selection => {
    this.setState({ error: '', selection }, this.focusTitle);
  };

  saveConsent = async consentStatus => {
    if (!this.canSubmit()) return;
    const { preview, submitConsent: save } = this.props;
    if (preview) {
      this.showConfirmation(consentStatus === 'provided' ? 'accepted' : 'declined');
      return;
    }
    const token = getToken(this.props);
    this.pendingTokens.add(token);
    this.setState({ error: '' });
    try {
      const { contactNumber } = this.state;
      await save(token, consentStatus, normaliseUKPhoneNumber(contactNumber));
    } finally {
      this.pendingTokens.delete(token);
    }
  };

  handleOptIn = event => {
    event.preventDefault();
    if (!this.canSubmit()) return;
    const { contactNumber } = this.state;

    if (!contactNumber.trim()) {
      this.setState({ error: 'Enter a telephone number to opt in.', selection: '' });
      document.getElementById('nms-telephone').focus();
      return;
    }

    if (!normaliseUKPhoneNumber(contactNumber)) {
      this.setState({ error: 'Enter a valid UK telephone number.', selection: '' });
      document.getElementById('nms-telephone').focus();
      return;
    }

    this.saveConsent('provided');
  };

  renderConfirmation = selection => {
    const accepted = selection === 'accepted';
    const Icon = accepted ? CheckIcon : ChatBubbleLeftIcon;

    return (
      <Confirmation>
        <Helmet>
          <title>{`${accepted ? 'Thank you for confirming' : 'Thanks for letting us know'} | Well Pharmacy`}</title>
        </Helmet>
        <ConfirmationIcon outcome={selection} aria-hidden="true">
          <Icon />
        </ConfirmationIcon>
        <ConfirmationTitle id="nms-confirmation-title" tabIndex="-1">
          {accepted ? 'Thank you for confirming' : 'Thanks for letting us know'}
        </ConfirmationTitle>

        <Callout>
          <Copy>
            {accepted
              ? 'We’ve received your request for support with your new medication.'
              : 'You’ve chosen not to receive New Medicine Service support at this time.'}
          </Copy>
        </Callout>

        <Section>
          {accepted ? (
            <Fragment>
              <SectionTitle>What happens next?</SectionTitle>
              <NextSteps>
                <li>
                  A member of our pharmacy team will contact you using the telephone number
                  you’ve provided to discuss the next steps.
                </li>
                <li>
                  Our pharmacist can answer questions and help you feel more confident
                  taking your medication.
                </li>
                <li>You don’t need to do anything else right now.</li>
              </NextSteps>
            </Fragment>
          ) : (
            <Copy>
              If you have any questions or concerns about your medication,
              our pharmacy team is still here to help.
            </Copy>
          )}
        </Section>

      </Confirmation>
    );
  };

  renderLinkState = selection => {
    const { title, message, nextStep } = linkStates[selection];
    const Icon = selection === 'already-responded' ? ClipboardDocumentCheckIcon : LinkExpiredIcon;

    return (
      <Confirmation>
        <Helmet>
          <title>{`${title} | Well Pharmacy`}</title>
        </Helmet>
        <ConfirmationIcon outcome={selection} aria-hidden="true">
          <Icon />
        </ConfirmationIcon>
        <ConfirmationTitle id="nms-confirmation-title" tabIndex="-1">{title}</ConfirmationTitle>
        <Callout><Copy>{message}</Copy></Callout>
        <Section><Copy>{nextStep}</Copy></Section>
      </Confirmation>
    );
  };

  renderForm() {
    const { contactNumber, error: validationError } = this.state;
    const { preview, nms } = this.props;
    const medicines = preview ? previewMedicines : nms.medicines;
    const submitting = !preview && (nms.submitting || this.pendingTokens.has(getToken(this.props)));
    const error = validationError || (!preview && nms.error) || '';

    return (
      <Fragment>
        <Introduction>
          <Heading
            title="Support with your New Medication"
            subtitle="Starting a new medicine? We’re here to help. Well Pharmacy offers a free NHS New Medicine Service to help you feel confident taking your new medication."
          />
        </Introduction>

        {medicines.length > 0 && (
          <Section aria-labelledby="nms-medications-heading">
            <SectionTitle id="nms-medications-heading">
              Our records suggest you may be eligible for support with the following medication:
            </SectionTitle>
            <ListWrapper>
              <Medicines>
                {medicines.map(medicine => (
                  <Medicine key={medicine.vmpId}>
                    <MedicineRowCell medicine={medicine} titleWrapper />
                  </Medicine>
                ))}
              </Medicines>
            </ListWrapper>
          </Section>
        )}

        <Section aria-labelledby="nms-support-heading">
          <SectionTitle id="nms-support-heading">How can we help?</SectionTitle>
          <NextSteps>
            <li>
              {medicines.length > 0
                ? 'If you are starting any of the medicines listed above for the first time,'
                : 'If you are starting a new medicine for the first time,'}
              {' '}
              our pharmacy team can offer
              {' '}
              <strong>free support</strong>
              {' '}
              through the NHS New Medicine Service.
            </li>
            <li>
              A pharmacist can contact you to discuss your medication, answer any questions
              and help with any concerns you may have.
            </li>
            <li>
              If you would like support, please enter your telephone number below
              and select
              {' '}
              <strong>“Yes, I’d like support.”</strong>
            </li>
          </NextSteps>

          <SupportForm onSubmit={this.handleOptIn} noValidate>
            <TextField
              key={getToken(this.props) || 'preview'}
              id="nms-telephone"
              name="contactNumber"
              inputType="tel"
              autoComplete="tel"
              placeholder="Your Telephone Number"
              aria-label="Your Telephone Number"
              disabled={submitting}
              value={contactNumber}
              onChange={value => this.setState({ contactNumber: value, error: '', selection: '' })}
              aria-describedby={error ? 'nms-telephone-hint nms-telephone-error' : 'nms-telephone-hint'}
              aria-invalid={Boolean(error)}
            />
            <Hint id="nms-telephone-hint">
              By selecting “Yes, I’d like support”, you agree to Well Pharmacy contacting you
              by telephone about the NHS New Medicine Service. For more information about
              how we use your personal information, please see our
              {' '}
              <Link href="https://well.co.uk/about-us/policies/privacy/">Privacy Notice</Link>
              .
            </Hint>
            <ErrorMessage id="nms-telephone-error" role="alert">{error}</ErrorMessage>

            <Actions>
              <PrimaryButton
                title="Yes, I'd like support"
                type="submit"
                disabled={submitting}
                loading={submitting && nms.pendingConsent === 'provided'}
              />
              <PrimaryButton
                title="No, thank you"
                type="button"
                onClick={() => this.saveConsent('notProvided')}
                disabled={submitting}
                loading={submitting && nms.pendingConsent === 'notProvided'}
              />
            </Actions>
          </SupportForm>
        </Section>
      </Fragment>
    );
  }

  render() {
    const { preview, nms } = this.props;
    const { selection: previewSelection } = this.state;
    const token = getToken(this.props);
    const selection = preview ? previewSelection : !token ? 'unavailable'
      : nms.token === token ? nms.status : 'loading';
    let page;
    if (linkStates[selection]) {
      page = this.renderLinkState(selection);
    } else if (['accepted', 'declined'].includes(selection)) {
      page = this.renderConfirmation(selection);
    } else if (['error', 'save-error'].includes(selection)) {
      page = (
        <Section>
          <SectionTitle id="nms-confirmation-title" tabIndex="-1">
            {selection === 'save-error' ? 'We couldn’t confirm your response' : 'We couldn’t check your link'}
          </SectionTitle>
          <Copy role="alert">{nms.error}</Copy>
          <Actions>
            <PrimaryButton
              title={selection === 'save-error' ? 'Check link again' : 'Try again'}
              onClick={this.loadAccess}
              type="button"
            />
          </Actions>
        </Section>
      );
    } else if (preview || selection === 'active') {
      page = this.renderForm();
    } else {
      page = <Section role="status"><Copy>Checking your link…</Copy></Section>;
    }

    return (
      <PageContainer hideHelp hideMobileMenu>
        <Helmet>
          <title>Support with your New Medication | Well Pharmacy</title>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="referrer" content="no-referrer" />
        </Helmet>
        <Content>
          <Illustration aria-hidden="true">
            <Bag />
            <Capsule />
          </Illustration>
          {page}

          <Footer>Well Pharmacy · New Medicine Service</Footer>
        </Content>
      </PageContainer>
    );
  }
}

NMSPage.propTypes = {
  initialSelection: PropTypes.oneOf(['', 'accepted', 'declined', 'expired', 'already-responded', 'unavailable']),
  preview: PropTypes.bool,
  nms: PropTypes.object,
  match: PropTypes.shape({ params: PropTypes.object }),
  fetchAccess: PropTypes.func.isRequired,
  submitConsent: PropTypes.func.isRequired,
};

NMSPage.defaultProps = {
  initialSelection: '',
  preview: false,
  nms: initialState,
  match: { params: {} },
};

const ConnectedNMSPage = connect(state => ({ nms: getNMSState(state) }), {
  fetchAccess,
  submitConsent,
})(NMSPage);

export const NMSFormPreview = () => <ConnectedNMSPage preview />;
export const NMSAcceptedPreview = () => <ConnectedNMSPage preview initialSelection="accepted" />;
export const NMSDeclinedPreview = () => <ConnectedNMSPage preview initialSelection="declined" />;
export const NMSExpiredPreview = () => <ConnectedNMSPage preview initialSelection="expired" />;
export const NMSAlreadyRespondedPreview = () => <ConnectedNMSPage preview initialSelection="already-responded" />;

export default ConnectedNMSPage;
